import React from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';

/**
 * Centralised <head> manager for SEO using @dr.pogodin/react-helmet.
 *
 * Two-layer SEO architecture:
 *   1. Bot-fallback layer: /public/index.html ships solid default <title>,
 *      <meta>, OG, Twitter, canonical and JSON-LD tags so non-JS bots see
 *      proper metadata on the first byte. Each default tag is marked
 *      data-seo-default="1".
 *   2. Runtime layer: /src/index.js strips those tagged defaults from <head>
 *      BEFORE React mounts. Helmet then owns <head> exclusively and emits
 *      unique per-route tags via this component (mounted once at the App
 *      level by <RouteAwareSeo />). No duplicates, canonical and og:url
 *      always reflect the current route.
 *
 * Why @dr.pogodin/react-helmet (not react-helmet-async):
 *   react-helmet-async is unmaintained (peer deps cap at React 18) and on
 *   React 19 it does not even reliably dedupe its own <title>/<script>
 *   children. @dr.pogodin/react-helmet is an actively maintained, React 19
 *   compatible drop-in successor with the same <HelmetProvider>/<Helmet>
 *   API. Note: like every Helmet variant, it only manages tags it itself
 *   renders — it does NOT touch pre-existing static <head> tags. That's
 *   why we strip them on boot in /src/index.js.
 */
const SITE_URL = 'https://fortuneautodesign.com';
const DEFAULT_IMAGE = `${SITE_URL}/assets/fortune-og.png`;

export const SeoHead = ({
  title,
  description,
  path = '',
  image,
  type = 'website',
  jsonLd,
  noindex = false,
}) => {
  const fullTitle = title
    ? `${title} | Fortune Auto Design`
    : 'Fortune Auto Design — Premium Body Kits, Carbon Fiber & Forged Wheels';
  const desc =
    description ||
    "Canada's largest selection of premium body kits, dry carbon fiber components, forged wheels and custom fabrication.";
  const url = `${SITE_URL}${path || ''}`;
  const og = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
    : DEFAULT_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      )}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Fortune Auto Design" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={og} />
      <meta property="og:url" content={url} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={og} />

      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
          .filter(Boolean)
          .map((obj, i) => (
            <script key={i} type="application/ld+json">
              {JSON.stringify(obj)}
            </script>
          ))}
    </Helmet>
  );
};

/**
 * Clean any product/collection copy into a search-friendly meta description:
 * strips HTML, decodes common entities, collapses whitespace/newlines, and
 * truncates on a word boundary (~155 chars) so snippets never cut mid-word.
 */
export const cleanDescription = (text, max = 155) => {
  if (!text) return '';
  let s = String(text)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&rsquo;|&lsquo;/gi, "'")
    .replace(/&mdash;|&ndash;/gi, '\u2013')
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.\-]+$/, '') + '\u2026';
};

/** Build a BreadcrumbList schema from an ordered list of {name, url} crumbs. */
export const buildBreadcrumbJsonLd = (crumbs) => {
  if (!crumbs || !crumbs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => {
      const item = { '@type': 'ListItem', position: i + 1, name: c.name };
      if (c.url) item.item = `${SITE_URL}${c.url}`;
      return item;
    }),
  };
};

/** Build a JSON-LD Product schema from one of our product objects. */
export const buildProductJsonLd = (product) => {
  if (!product) return null;
  const img = (product.images && product.images[0]) || DEFAULT_IMAGE;
  const price = product.variants?.[0]?.price ?? product.price;
  const availability =
    product.status === 'out-of-stock'
      ? 'https://schema.org/OutOfStock'
      : 'https://schema.org/InStock';
  const imageUrl = img && img.startsWith('http') ? img : `${SITE_URL}${img}`;
  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    image: [imageUrl],
    description: (product.description || '').slice(0, 500),
    sku: product.sku || product.id,
    brand: { '@type': 'Brand', name: product.brand || 'Fortune Auto Design' },
    category: product.category,
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/product/${product.slug || product.id}`,
      priceCurrency: 'USD',
      price: String(price),
      availability,
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'Fortune Auto Design' },
    },
  };
};

/** Build a JSON-LD Brand schema for a manufacturer brand hub page. */
export const buildBrandJsonLd = (brand) => {
  if (!brand) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Brand',
    name: brand.name,
    description: (brand.intro && brand.intro[0]) || brand.metaDescription,
    url: `${SITE_URL}/brands/${brand.slug}`,
    logo: `${SITE_URL}/assets/fortune-og.png`,
  };
};

/** Build a JSON-LD ItemList of product URLs (for brand/collection hub pages). */
export const buildItemListJsonLd = (products, max = 60) => {
  if (!products || !products.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    numberOfItems: products.length,
    itemListElement: products.slice(0, max).map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE_URL}/product/${p.slug || p.id}`,
      name: p.name,
    })),
  };
};

export default SeoHead;
